import React from "react";
import { useLogoBrightness } from "@/lib/useLogoBrightness";

/**
 * LogoWithHalo — renders an entity logo and, ONLY when the image is
 * classified as predominantly dark, layers a subtle soft radial glow
 * behind it so the mark stays readable on the dark HQ theme.
 *
 * Contract:
 *   • The wrapper element sizes/positions the logo — pass whatever
 *     classes you want via `wrapperClassName`. The halo sizes itself
 *     to the wrapper and never grows the layout box (uses absolute
 *     positioning with `inset-0`).
 *   • `variant="detail"` slightly amplifies the halo (larger image
 *     surface), `variant="compact"` keeps it tight for small tiles.
 *   • If the browser can't measure the image (CORS taint, decode
 *     error, empty SVG, timeout), the halo silently stays off — the
 *     logo renders exactly as before. No console noise, no layout jump.
 */
export default function LogoWithHalo({
  src,
  alt,
  wrapperClassName = "",
  imgClassName = "",
  imgStyle,
  variant = "detail",
  draggable = false,
  testid,
}) {
  const { classification } = useLogoBrightness(src);
  const isDark = classification === "dark";
  return (
    <div
      className={`relative ${wrapperClassName}`}
      data-testid={testid}
      data-logo-halo={isDark ? "on" : "off"}
      data-logo-classification={classification}
    >
      {isDark && <Halo variant={variant} />}
      <img
        src={src}
        alt={alt}
        className={`relative ${imgClassName}`}
        style={imgStyle}
        draggable={draggable}
      />
    </div>
  );
}

function Halo({ variant }) {
  // Two variants:
  //  • detail  — large hero surface; halo can be a touch stronger to
  //              read behind a wide black mark.
  //  • compact — small 32-40 px tile; keep the glow tight so it never
  //              paints outside the tile's rounded corners.
  const isDetail = variant === "detail";
  const inner = isDetail ? "0.32" : "0.28";
  const mid = isDetail ? "0.14" : "0.12";
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        background: `radial-gradient(circle at center, rgba(255,255,255,${inner}) 0%, rgba(255,255,255,${mid}) 42%, rgba(255,255,255,0) 72%)`,
      }}
    />
  );
}
